import React, { useState, useEffect, useRef } from 'react';
import { backendUrl } from '../utils/variables';
import 'bootstrap/dist/css/bootstrap.min.css';

const PeopleToFollow = ({ currentUserId, refresh_insights }) => {
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  const fetchSuggestedUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/suggested_users.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUserId,
          limit: 15 // More users for a good scrolling experience
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuggestedUsers(data.users);
      } else {
        setError('No users to suggest at this time');
      }
    } catch (err) {
      setError('Failed to load suggestions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToProfile = (username) => {
    window.location.href = `/${username}`;
  };

  const handleFollow = async (userId) => {
    try {
      const response = await fetch(`${backendUrl}/toggle_follow.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          my_id: currentUserId,
          profile_id: userId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the UI to show the user has been followed
         setSuggestedUsers(suggestedUsers.map(user => 
          user.id === userId ? { ...user, followed: true } : user
        ));

        refresh_insights()
      }
    } catch (err) {
      console.error('Failed to follow user:', err);
    }
  };

  useEffect(() => {
    fetchSuggestedUsers();
  }, [currentUserId]);

  // Enable horizontal scroll with mouse drag
  useEffect(() => {
    const slider = scrollContainerRef.current;
    if (!slider) return;
    
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener('mousedown', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    
    slider.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('active');
    });
    
    slider.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('active');
    });
    
    slider.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    });

    return () => {
      slider.removeEventListener('mousedown', () => {});
      slider.removeEventListener('mouseleave', () => {});
      slider.removeEventListener('mouseup', () => {});
      slider.removeEventListener('mousemove', () => {});
    };
  }, []);

  if (loading ) return (
    <div className="d-flex justify-content-center align-items-center p-4">
      <div className="spinner-border text-main" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
  
  if (error && suggestedUsers.length === 0) {
    return <span>{''}</span>;
  }

  return (
    <div className="bg-white_ rounded shadow_ p-3 mb-4">
      <h5 className="mb-3 fw-bold">Suggested for you</h5>
      
      <div 
        ref={scrollContainerRef}
        className="d-flex overflow-auto pb-2" 
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          cursor: 'grab',
          scrollBehavior: 'smooth'
        }}
      >
        {suggestedUsers.map(user => (
          <div 
            key={user.id} 
            className="d-flex flex-column align-items-center me-4" 
            style={{ minWidth: '150px', border: '1px solid #dacfcfd9', borderRadius: '12px', padding: '12px' }}
          >
            <div className="position-relative mb-2">
              <div 
                className="rounded-circle overflow-hidden"
                style={{ width: '120px', height: '120px', border: '0px solid #efefef' }}
              >
                {user.photo ? (
                  <img 
                    src={user.photo} 
                    alt={user.username} 
                    className="w-100 h-100 object-fit-cover"
                    onClick={() => handleGoToProfile(user.username)}
                  />
                ) : (
                  <div className="w-100 h-100 bg-light d-flex align-items-center justify-content-center">
                    <span className="text-secondary fs-3 fw-bold">{user.username.charAt(0).toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>
            
            <p className="mb-1 text-center text-truncate" style={{ maxWidth: '130px' }}>
              <strong>{user.username}</strong>
            </p>
            
            <p className="text-muted small text-center text-truncate mb-2" style={{ maxWidth: '130px' }}>
              {user.first_name} {user.last_name}
            </p>
            
            <button
              onClick={() => handleFollow(user.id)}
              className={`btn btn-sm ${user.followed ? 'btn-outline-secondary' : 'btn-primary b-primary'} w-100`}
              disabled={user.followed}
            >
              {user.followed ? 'Following' : 'Follow'}
            </button>
          </div>
        ))}
        
        {suggestedUsers.length === 0 && (
          <p className="text-muted text-center w-100">No suggestions available right now</p>
        )}
      </div>
      
      {suggestedUsers.length > 0 && (
        <div className="text-center mt-3 d-none">
          <button 
            onClick={fetchSuggestedUsers}
            className="btn btn-link text-decoration-none"
          >
            Refresh suggestions
          </button>
        </div>
      )}

      <style jsx>{`
        /* Hide scrollbar for Chrome, Safari and Opera */
        div::-webkit-scrollbar {
          display: none;
        }
        
        /* Active state styles for the container when being dragged */
        .active {
          cursor: grabbing !important;
        }
      `}</style>
    </div>
  );
};

export default PeopleToFollow;